import { SongAdCompositionScene, SongAdCtaCard } from './types';

/**
 * 詳細編集/プレビューでのメディアURL安定化（v5 k）。
 *
 * 原因（advisor-notes.md (2) で実コード裏取り済み・EditorStep/PreviewStep双方で確認）:
 * `SongAdProject::temporaryUrl()`（backend）はCDN無効時、呼ぶたびに新しい presigned URL
 * （新しい `X-Amz-Signature` クエリ文字列）を発行する。自動保存やポーリングで project を
 * 取得し直すたびに `song_url`/`scenes[].storyboard.image.url`/`scenes[].clip.video.url` の
 * 文字列が変わり、Remotion の `<Audio src>`/`<Video src>`/`<Img src>` がリロードされて
 * 再生位置が先頭へ戻る。
 *
 * 対策: URLの `?` より前（S3オブジェクトパス。presigned再発行でも不変）をキーに、
 * 「最初に見た完全URL」をモジュールキャッシュで保持し続ける。同じオブジェクトを指す限り
 * `src` の文字列値が変わらないため、React/Remotion 側は再ロードしない。
 * presignedの有効期限は60分なので、45分でキャッシュを失効させて次回呼び出し時に
 * 最新URLへ更新する（＝有効期限切れの表示を防ぐ）。加えて、メディア要素の `error` イベントからも
 * `invalidateStableMediaUrl` で即時失効させられる（SongAdComposition.tsx から呼ぶ）。
 *
 * CDN有効時（`SongAdProject::temporaryUrl` がCDN固定URLを返す場合）はURL自体が変わらないため、
 * このキャッシュは実質no-op（常に同じ値を格納し続けるだけ）で無害。
 */

const CACHE_TTL_MS = 45 * 60 * 1000; // presignedの有効期限(60分)より短く設定し、期限切れ前に更新する

interface CacheEntry {
  url: string;
  cachedAt: number;
}

const cache = new Map<string, CacheEntry>();

/** presigned URLの `?` より前（S3オブジェクトパス）をキャッシュキーにする */
export function extractMediaCacheKey(url: string): string {
  const qIndex = url.indexOf('?');
  return qIndex === -1 ? url : url.slice(0, qIndex);
}

/**
 * 同じS3オブジェクトを指すURLなら、TTL内は最初に見た完全URLを返し続ける（文字列値を安定させる）。
 * TTLを過ぎたら渡された最新URLで更新する。
 */
export function stableMediaUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const key = extractMediaCacheKey(url);
  const now = Date.now();
  const existing = cache.get(key);
  if (existing && now - existing.cachedAt < CACHE_TTL_MS) {
    return existing.url;
  }
  cache.set(key, { url, cachedAt: now });
  return url;
}

/** メディア要素の error イベントから呼ぶ。次回 stableMediaUrl() 呼び出し時に最新URLへ強制更新させる */
export function invalidateStableMediaUrl(url: string | null | undefined): void {
  if (!url) return;
  cache.delete(extractMediaCacheKey(url));
}

/** テスト用: モジュールキャッシュをリセットする（テストケース間の干渉を防ぐ） */
export function __resetStableMediaUrlCacheForTests(): void {
  cache.clear();
}

/**
 * EditorStep/PreviewStep 共通: deriveCompositionScenes/deriveCtaCard の出力と song_url へ
 * まとめて安定化を適用する。scenes/cta の配列・オブジェクトの参照は project 更新のたびに
 * 新しくなるが、中身のURL文字列値は（同一S3オブジェクトである限り）TTL内は変わらない。
 */
export function stabilizeCompositionMedia(
  scenes: SongAdCompositionScene[],
  cta: SongAdCtaCard | null,
  audioUrl: string | null
): { scenes: SongAdCompositionScene[]; cta: SongAdCtaCard | null; audioUrl: string | null } {
  return {
    scenes: scenes.map((scene) => ({
      ...scene,
      imageUrl: stableMediaUrl(scene.imageUrl),
      videoUrl: stableMediaUrl(scene.videoUrl),
    })),
    cta: cta
      ? {
          ...cta,
          backgroundImageUrl: stableMediaUrl(cta.backgroundImageUrl),
          logoUrl: stableMediaUrl(cta.logoUrl),
        }
      : null,
    audioUrl: stableMediaUrl(audioUrl),
  };
}
